import { GisMapPane } from './GisMapPane'
import { DetailDrillHost } from './panel/detail/DetailDrillHost'

import type { GisLens, GisOverview, GisScope, GisTab } from '../api/types'
import type { GisSelection } from '../hooks/useGisSelection'
import type { MapMetric } from '../layers/mandalChoroplethLayer'
import type { DetailFrame, HodOpenRef } from '../lib/detail-stack'

export interface GisMapRegionProps {
  overview: GisOverview
  lens: GisLens
  mapMetric: MapMetric
  attentionByName: Map<string, number>
  issueCountByName: Map<string, number>
  issuesPage: { hasMore: boolean; totalCount: number } | undefined
  issuesBySite: Map<string, { open: number; critical: number }>
  issuesFocus: boolean
  coverageFocus: boolean
  selection: GisSelection
  activeMandals: readonly string[]
  scope: GisScope
  /** The open drill frame (if any); issue/HOD frames pop up OVER the map, site frames don't. */
  frame: DetailFrame | null
  backLabel: string
  onLensChange: (tab: GisTab) => void
  onSelectMandal: (name: string) => void
  onSelectSite: (id: string) => void
  onClearSelection: () => void
  onCloseMap: () => void
  onDetailBack: () => void
  onDetailClose: () => void
  onOpenIssue: (id: string, siteId?: string | null) => void
  onOpenHod: (hod: HodOpenRef) => void
  onSelectOfficer: (id: string, name: string) => void
}

/** The RIGHT pane of the V5 GIS layout (Split only): the map, plus the issue/HOD detail pop-up
 *  floating over it — a dim click-to-close backdrop under a rounded card hosting the drill body. */
export function GisMapRegion(p: GisMapRegionProps) {
  const overlayFrame = p.frame && p.frame.kind !== 'site' ? p.frame : null
  return (
    <div className="relative min-w-0 flex-1">
      <GisMapPane
        overview={p.overview}
        lens={p.lens}
        mapMetric={p.mapMetric}
        attentionByName={p.attentionByName}
        issueCountByName={p.issueCountByName}
        issuesCapped={p.issuesPage?.hasMore ?? false}
        issuesTotalCount={p.issuesPage?.totalCount ?? 0}
        issuesBySite={p.issuesBySite}
        issuesFocus={p.issuesFocus}
        coverageFocus={p.coverageFocus}
        selection={p.selection}
        activeMandals={p.activeMandals}
        onLensChange={p.onLensChange}
        onSelectMandal={p.onSelectMandal}
        onSelectSite={p.onSelectSite}
        onClearSelection={p.onClearSelection}
        onCloseMap={p.onCloseMap}
      />
      {overlayFrame && (
        <div className="absolute inset-0 z-20">
          <button
            type="button"
            aria-label="Close detail"
            onClick={p.onDetailClose}
            className="absolute inset-0 bg-black/25"
          />
          <div className="absolute inset-3 flex flex-col overflow-hidden rounded-[14px] border border-gis-line bg-gis-panel shadow-2xl">
            <div className="relative min-h-0 flex-1">
              <DetailDrillHost
                frame={overlayFrame}
                backLabel={p.backLabel}
                sites={p.overview.sites}
                scope={p.scope}
                onBack={p.onDetailBack}
                onOpenIssue={p.onOpenIssue}
                onOpenHod={p.onOpenHod}
                onSelectOfficer={p.onSelectOfficer}
                onSelectMandal={p.onSelectMandal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
